/**
 * Stores > HQ questions at /stores/hq/questions.
 *
 * The queue of store questions, ordered urgency-desc then oldest-first (the
 * backend already orders it this way; the UI preserves that order). Filters for
 * status and urgency. Each open question has an inline answer composer; posting
 * an answer flips the question to answered and drops it out of the open view.
 * Answered items render collapsed.
 *
 * Gated by stores:hq. 404/503 -> not-available placeholder.
 */

import { useState } from 'react';
import { Pill } from '@/components/Pill';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { useSession } from '@/lib/useSession';
import { hasAccessSlug } from '@/lib/toolVisibility';
import { formatDateTime } from '@/lib/dateUtils';
import {
  useHqQuestions,
  useAnswerQuestion,
  useCloseQuestion,
  isNotAvailable,
  URGENCY_TONES,
  urgencyLabel,
  THREAD_STATUS_TONES,
  type HqQuestion,
} from '@/lib/useStores';
import {
  StoresHeader,
  AccessDeniedCard,
  NotAvailableCard,
  ErrorCard,
  primaryBtnCls,
  secondaryBtnCls,
  inputCls,
} from './storesShared';

export function HqQuestions() {
  const { user } = useSession();
  const canView = hasAccessSlug(user?.permissions, 'stores.questions', user?.superAdmin);

  const [status, setStatus] = useState('open');
  const [urgency, setUrgency] = useState('');

  const q = useHqQuestions({ status: status || undefined, urgency: urgency || undefined });

  if (user && !canView) return <AccessDeniedCard what="The question queue" />;
  if (isNotAvailable(q.error)) {
    return (
      <div>
        <StoresHeader title="Questions" actions={<ToolPinToggle toolSlug="stores-hq-questions" toolName="Questions" />} />
        <NotAvailableCard what="The question queue" />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl">
      <StoresHeader
        title="Questions"
        subtitle="Store questions, most urgent and oldest first. Answer to close them out."
        actions={<ToolPinToggle toolSlug="stores-hq-questions" toolName="Questions" />}
      />

      <div className="mb-4 grid grid-cols-2 items-end gap-3 sm:flex sm:flex-wrap">
        <div className="min-w-0">
          <label htmlFor="status" className="mb-1 block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">Status</label>
          <select
            id="status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full rounded border border-amp-line bg-amp-white px-3 py-2 text-sm text-amp-ink sm:w-auto sm:min-w-[140px] sm:py-1.5 dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
          >
            <option value="open">Open</option>
            <option value="answered">Answered</option>
            <option value="closed">Closed</option>
            <option value="">All</option>
          </select>
        </div>
        <div className="min-w-0">
          <label htmlFor="urgency" className="mb-1 block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">Urgency</label>
          <select
            id="urgency"
            value={urgency}
            onChange={(e) => setUrgency(e.target.value)}
            className="w-full rounded border border-amp-line bg-amp-white px-3 py-2 text-sm text-amp-ink sm:w-auto sm:min-w-[140px] sm:py-1.5 dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
          >
            <option value="">All urgencies</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="normal">Normal</option>
            <option value="low">Low</option>
          </select>
        </div>
      </div>

      {q.isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-lg bg-amp-cream dark:bg-amp-dark-surface-warm" />
          ))}
        </div>
      ) : q.error ? (
        <ErrorCard message={q.error.message} />
      ) : q.questions.length === 0 ? (
        <div className="rounded-lg border border-dashed border-amp-line px-4 py-10 text-center dark:border-amp-dark-line">
          <p className="text-sm font-medium text-amp-ink dark:text-amp-dark-ink">No questions here</p>
          <p className="mt-1 text-[13px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Nothing matches this filter. When a store asks a question it lands here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {q.questions.map((question) => (
            <QuestionCard key={question.id} q={question} />
          ))}
        </div>
      )}
    </div>
  );
}

function QuestionCard({ q }: { q: HqQuestion }) {
  const isOpen = q.status === 'open';
  const [expanded, setExpanded] = useState(isOpen);
  const [answer, setAnswer] = useState('');
  const answerM = useAnswerQuestion();
  const closeM = useCloseQuestion();

  const submit = () => {
    const text = answer.trim();
    if (!text) return;
    answerM.mutate(
      { id: q.id, body: text },
      { onSuccess: () => setAnswer('') },
    );
  };

  return (
    <div className="rounded-lg border border-amp-line bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <div className="flex flex-wrap items-center gap-2">
        <Pill tone={URGENCY_TONES[q.urgency]}>{urgencyLabel(q.urgency)}</Pill>
        <Pill tone={THREAD_STATUS_TONES[q.status]}>{q.status.charAt(0).toUpperCase() + q.status.slice(1)}</Pill>
        {q.question_category && (
          <span className="text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">{q.question_category}</span>
        )}
        <span className="ml-auto text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
          {formatDateTime(q.created_at)}
        </span>
      </div>

      <p className="mt-2 text-[13px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
        <span className="font-medium text-amp-ink dark:text-amp-dark-ink">{q.store_name}</span>
        {q.author_name ? ` · ${q.author_name}` : ''}
      </p>

      <p className="mt-1 whitespace-pre-wrap text-[15px] text-amp-ink dark:text-amp-dark-ink">{q.body}</p>

      {isOpen ? (
        <div className="mt-3">
          {/* Stays a plain box. This answer is read back inside the shared chat
              thread in both apps and in the askhq@ notification email, none of
              which render HTML, so markup would reach store staff as raw tags.
              Chat is also explicitly out of scope for this change. */}
          <textarea
            rows={2}
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder="Answer this question..."
            className={`${inputCls} min-h-[72px] py-2 text-[15px] sm:min-h-0 sm:py-1.5 sm:text-sm`}
          />
          <div className="mt-2 flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={submit}
              disabled={answerM.isPending || !answer.trim()}
              className={`${primaryBtnCls} min-h-[44px] justify-center py-2.5 sm:min-h-0 sm:py-1.5`}
            >
              {answerM.isPending ? 'Posting...' : 'Post answer'}
            </button>
            <button
              type="button"
              onClick={() => closeM.mutate({ id: q.id })}
              disabled={closeM.isPending || answerM.isPending}
              title="Close without an official answer (resolved out-of-band)"
              className={`${secondaryBtnCls} min-h-[44px] justify-center py-2.5 sm:min-h-0 sm:py-1.5`}
            >
              {closeM.isPending ? 'Closing...' : 'Close'}
            </button>
          </div>
          <p className="mt-1 text-[11px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Close marks the question resolved without an official answer.
          </p>
          {answerM.error && (
            <p className="mt-2 text-[13px] text-red-700 dark:text-red-300">Could not post: {answerM.error.message}</p>
          )}
          {closeM.error && (
            <p className="mt-2 text-[13px] text-red-700 dark:text-red-300">Could not close: {closeM.error.message}</p>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 text-xs text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
        >
          {expanded ? 'Hide' : 'Show details'}
        </button>
      )}
    </div>
  );
}
